import { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import { NULL_VALUE } from 'constants/displayConstants';
import {
  useUpdateCustomEvent,
  type CustomEventSummary,
} from 'hooks/customEvents/useCustomEvents';
import { parseError } from 'utils/parseTypedErrorMessage';
import { AddEventAssetsDialog } from './AddEventAssetsDialog';
import { EventAssetCard } from './EventAssetCard';
import { REMOVE_ASSET_CONFIRMATION } from './eventConfirmations';

type EventAssetsAtRiskProps = {
  event: CustomEventSummary;
};

export const EventAssetsAtRisk: React.FC<EventAssetsAtRiskProps> = ({
  event,
}) => {
  const { data: resourceSets = [] } = useResourceSets();
  const updateEvent = useUpdateCustomEvent(event.id);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [removeId, setRemoveId] = useState<UUID | null>(null);

  const currentIds = event.resourceSets.map((item) => item.resourceSetId);
  const addOptions = resourceSets
    .filter(
      (item) => !item.archived && !currentIds.includes(item.resource_set_id)
    )
    .map((item) => ({ value: item.resource_set_id, label: item.name }));

  const saveResourceSets = async (resourceSetIds: UUID[]) => {
    try {
      await updateEvent.mutateAsync({ resourceSetIds });
      return true;
    } catch (error) {
      toast.error(parseError(error) ?? 'Failed to update assets at risk.');
      return false;
    }
  };

  const handleAdd = async (ids: UUID[]) => {
    if (await saveResourceSets([...currentIds, ...ids])) {
      setShowAddDialog(false);
    }
  };

  const handleRemove = async () => {
    const id = removeId;
    setRemoveId(null);
    if (id) await saveResourceSets(currentIds.filter((item) => item !== id));
  };

  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="tw:font-semibold">Assets at Risk</CardTitle>
        <CardAction>
          <Button
            variant="outline"
            size="sm"
            disabled={updateEvent.isPending}
            onClick={() => setShowAddDialog(true)}
          >
            <Plus data-icon="inline-start" />
            Add
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        {event.resourceSets.length > 0 ? (
          <div className="tw:grid tw:gap-2">
            {event.resourceSets.map((item) => {
              const resourceSet = resourceSets.find(
                (set) => set.resource_set_id === item.resourceSetId
              );
              return (
                <EventAssetCard
                  key={item.resourceSetId}
                  name={item.name ?? resourceSet?.name ?? NULL_VALUE}
                  resourceSet={resourceSet}
                  disabled={updateEvent.isPending}
                  onRemove={() => setRemoveId(item.resourceSetId)}
                />
              );
            })}
          </div>
        ) : (
          <div className="tw:text-muted-foreground">No assets at risk.</div>
        )}
      </CardContent>
      <AddEventAssetsDialog
        open={showAddDialog}
        options={addOptions}
        isPending={updateEvent.isPending}
        onClose={() => setShowAddDialog(false)}
        onAdd={handleAdd}
      />
      <AlertDialog
        open={removeId !== null}
        onOpenChange={(open) => !open && setRemoveId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {REMOVE_ASSET_CONFIRMATION.dialogTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {REMOVE_ASSET_CONFIRMATION.dialogContent}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {REMOVE_ASSET_CONFIRMATION.dialogCloseText}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => void handleRemove()}
            >
              {REMOVE_ASSET_CONFIRMATION.dialogButtonText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
